import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BrandedCard, BrandedButton, BootPulse } from '../components';
import { kratosService, KratosLogoutFlow } from '../services/kratosService';
import './Logout.css';

/**
 * /logout — confirm, then consume the Kratos browser logout flow and
 * redirect to /login.
 */
export const Logout: React.FC = () => {
  const navigate = useNavigate();
  const [logoutFlow, setLogoutFlow] = useState<KratosLogoutFlow | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        const flow = await kratosService.initLogoutFlow();
        setLogoutFlow(flow);
      } catch (err) {
        // Kratos may be unavailable (fallback-auth tenants) — sign-out can
        // still proceed locally, so this isn't treated as fatal.
        setError(err instanceof Error ? err.message : '');
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  const handleConfirm = async () => {
    setSubmitting(true);
    setError('');

    try {
      sessionStorage.removeItem('access_token');
      if (logoutFlow) {
        // Hands off to Kratos, which clears the cookie and redirects to the
        // configured post-logout URL (kratos.yml -> selfservice.flows.logout).
        kratosService.submitLogout(logoutFlow.logout_url);
        return;
      }
      navigate('/login');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-out failed. Please try again.');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <BrandedCard>
        <BootPulse label="Preparing sign-out" />
      </BrandedCard>
    );
  }

  return (
    <BrandedCard title="Sign Out">
      {error && (
        <div className="logout-error" role="alert">
          {error}
        </div>
      )}

      <p className="logout-message">Are you sure you want to sign out?</p>

      <div className="logout-actions">
        <BrandedButton type="button" variant="primary" fullWidth onClick={handleConfirm} disabled={submitting}>
          {submitting ? 'Signing out...' : 'Sign out'}
        </BrandedButton>

        <a href="/settings" className="logout-link">
          Cancel
        </a>
      </div>
    </BrandedCard>
  );
};
